/**
 * PURE derivations over the opaque `results.aiReportData` payload for the
 * FULL-SCREENING surfaces (FULLSCREEN-DATA-MAP §2/§3/§5–§9) — plain `.ts`,
 * client-safe, no db/server imports (the display.ts / verification-display.ts
 * idiom). Components bind these views and keep zero parsing logic.
 *
 * GATING: derivations here don't gate by screening kind — callers render
 * these surfaces ONLY when `screeningKind === 'full'` (the converted-app
 * invariant), with ONE exception: Risk Flags, whose nuanced kind gate is the
 * explicit `shouldShowRiskFlags` helper below (2026-07-17: pre-screen
 * APPLICANT-type applications surface existing flags too). Every function is
 * never-throw salvage: the payload's numbers
 * may arrive as strings (applicant flow stores strings; upstream payloads
 * vary), malformed entries degrade to null/'' instead of crashing.
 *
 * v1 truth at 4cbfe7e: report-data-helpers.ts (extractPredictions,
 * deriveDebtSummary, deriveRiskFlags, deriveReportData) +
 * DebtSummarySection.tsx / RiskFlagsSection.tsx render sites.
 */
import { extractPredictions } from '#app/utils/report-data-helpers'
import type { CreditSummary, ScreeningKind } from './types'

type AiReportData = Record<string, unknown> | null | undefined

/** number|numeric-string → finite number; everything else → null. */
const toAmount = (raw: unknown): number | null => {
	if (typeof raw === 'number') return Number.isFinite(raw) ? raw : null
	if (typeof raw !== 'string' || raw.trim() === '') return null
	const parsed = Number(raw.replace(/[$,%\s]/g, ''))
	return Number.isFinite(parsed) ? parsed : null
}

/** string → trimmed; number → stringified; everything else → ''. */
const toText = (raw: unknown): string => {
	if (typeof raw === 'string') return raw.trim()
	if (typeof raw === 'number' && Number.isFinite(raw)) return String(raw)
	return ''
}

const toRowObjects = (raw: unknown): Record<string, unknown>[] =>
	Array.isArray(raw)
		? raw.filter(
				(entry): entry is Record<string, unknown> =>
					entry != null && typeof entry === 'object' && !Array.isArray(entry),
			)
		: []

// ---------------------------------------------------------------------------
// §3 AI Generated Summary (future `predictions.summary` field)
// ---------------------------------------------------------------------------

/** Narrative paragraph; null until the upstream payload carries it — the
 * card presence-gates on this INSIDE the kind gate (map §3). */
export function deriveAiSummary(aiReportData: AiReportData): string | null {
	const summary: unknown = extractPredictions(aiReportData ?? null)?.summary
	const text = typeof summary === 'string' ? summary.trim() : ''
	return text || null
}

/**
 * Tenant orgs piloting the AI backend's `predictions.aiGeneratedSummary`
 * narrative (currently Rentpedia). While the pilot runs, ONLY these tenants
 * surface the new field so its copy can be verified in production; everyone
 * else keeps `predictions.summary` untouched. Launching for all landlords =
 * make `resolveAiSummaryText` unconditional and delete this list.
 */
export const AI_GENERATED_SUMMARY_PILOT_TENANT_IDS: readonly string[] = [
	'y5hbt4ycp0pj0u2e5fw13zpe', // Rentpedia
]

export function isAiGeneratedSummaryPilotTenant(
	tenantId: string | null | undefined,
): boolean {
	return !!tenantId && AI_GENERATED_SUMMARY_PILOT_TENANT_IDS.includes(tenantId)
}

/** The NEW AI-backend narrative (`predictions.aiGeneratedSummary`) — same
 * salvage as deriveAiSummary; absent on pre-pilot payloads. */
export function deriveAiGeneratedSummary(
	aiReportData: AiReportData,
): string | null {
	const predictions = extractPredictions(aiReportData ?? null) as
		| (Record<string, unknown> & { aiGeneratedSummary?: unknown })
		| null
		| undefined
	const summary: unknown = predictions?.aiGeneratedSummary
	const text = typeof summary === 'string' ? summary.trim() : ''
	return text || null
}

/**
 * The narrative the AI Summary card actually shows — shared by the page card
 * and the individual PDF so the two can't drift. Pilot tenants PREFER the new
 * `aiGeneratedSummary` (that is what the pilot verifies) and fall back to the
 * classic `summary`; every other tenant sees `summary` only (current flow).
 */
export function resolveAiSummaryText(input: {
	aiReportData: AiReportData
	aiGeneratedPilot: boolean
}): string | null {
	if (input.aiGeneratedPilot) {
		return (
			deriveAiGeneratedSummary(input.aiReportData) ??
			deriveAiSummary(input.aiReportData)
		)
	}
	return deriveAiSummary(input.aiReportData)
}

// ---------------------------------------------------------------------------
// §2 Header card — score + Guarantor Recommended chip
// ---------------------------------------------------------------------------

/**
 * v1's full score chain (map §2): `predictions.applicant.trueTenantScore ||
 * application.score || 0`, where application.score already folds
 * overallScore ?? screeningScore (compose). A guarantor-shaped payload (no
 * applicant, has guarantor) zeroes the report score so the chain falls to
 * the application score (helpers deriveReportData:230/254-256 + v1:5293).
 */
export function deriveHeaderScore(
	aiReportData: AiReportData,
	applicationScore: number | null,
): number {
	const predictions = extractPredictions(aiReportData ?? null)
	const isGuarantorResponse =
		!predictions?.applicant && Boolean(predictions?.guarantor)
	const reportScore = isGuarantorResponse
		? 0
		: (toAmount(predictions?.applicant?.trueTenantScore) ?? 0)
	return reportScore || applicationScore || 0
}

/**
 * The guarantor-recommendation card only shows at/above this score — the
 * legacy `GuarantorScore` chip's threshold (ui/guarantor-score.tsx). A low
 * guarantor score means a guarantor isn't really warranted, so the card stays
 * hidden (a guarantor that was nonetheless REQUESTED still surfaces its status,
 * gated separately on `guarantorRequests.requested`).
 */
export const GUARANTOR_RECOMMEND_MIN_SCORE = 50

/** Raw-score predicate (combined surface has no aiReportData/applicationType). */
export function isGuarantorRecommended(score: number | null): boolean {
	return (score ?? 0) >= GUARANTOR_RECOMMEND_MIN_SCORE
}

/**
 * v1's `hasGuarantorRecommendation`, individual branch (v1:4956-4963), gated to
 * `guarantorScore >= GUARANTOR_RECOMMEND_MIN_SCORE` (v1 used `> 0`; the card now
 * follows the legacy chip threshold). Hidden for guarantor applicants (they
 * cannot request another guarantor on their own report).
 */
export function deriveGuarantorRecommended(
	aiReportData: AiReportData,
	applicationType: 'primary' | 'co_applicant' | 'guarantor',
): boolean {
	if (applicationType === 'guarantor') return false
	const predictions = extractPredictions(aiReportData ?? null)
	return isGuarantorRecommended(
		toAmount(predictions?.applicant?.guarantorScore),
	)
}

/** The raw guarantor-recommendation score (0–100) for the individual report —
 * `predictions.applicant.guarantorScore`; null for guarantor applicants or
 * when absent. Drives the chip COLOR via guarantorScoreTone. */
export function deriveGuarantorScore(
	aiReportData: AiReportData,
	applicationType: 'primary' | 'co_applicant' | 'guarantor',
): number | null {
	if (applicationType === 'guarantor') return null
	const predictions = extractPredictions(aiReportData ?? null)
	return toAmount(predictions?.applicant?.guarantorScore)
}

/**
 * Guarantor ACCEPTABILITY verdict for a guarantor's OWN report —
 * `predictions.guarantorAccessibility.status` (a string like "Acceptable" /
 * "Not Acceptable"). A guarantor is scored on acceptability, NOT a 0–100 score,
 * so their report surfaces this verdict where an applicant shows the score
 * (matches the legacy IndividualReportView "Guarantor Acceptability" card).
 * `acceptable` is true only for an explicit "acceptable" (never "not acceptable").
 */
export type GuarantorAcceptability = {
	status: string | null
	acceptable: boolean
	hasVerdict: boolean
}

export function deriveGuarantorAcceptability(
	aiReportData: AiReportData,
): GuarantorAcceptability {
	const predictions = extractPredictions(aiReportData ?? null)
	const access = predictions?.guarantorAccessibility as
		| { status?: unknown }
		| undefined
	const status = toText(access?.status) || null
	const lower = (status ?? '').toLowerCase()
	return {
		status,
		acceptable: lower.includes('acceptable') && !lower.includes('not'),
		hasVerdict: status != null,
	}
}

/**
 * What the header's guarantor chip should say for a HOUSEHOLD (client
 * 2026-08-27). Once guarantors exist, the chip stops being a recommendation
 * and becomes a verdict on the people who actually joined:
 *
 *   every guarantor acceptable   → 'acceptable'   (green, shield-check)
 *   none acceptable              → 'unacceptable' (red, shield-x)
 *   some but not all             → 'mixed'        (amber, shield-alert)
 *   joined but not yet judged    → 'mixed'        — present, unproven
 *   no guarantor at all          → 'recommended'  when one was requested or
 *                                  the applicant's guarantor score earns it,
 *                                  otherwise no chip.
 *
 * A RECOMMENDATION is never green: green reads "this household is covered",
 * which is exactly what an outstanding recommendation is not. Low scores that
 * `guarantorScoreTone` would paint green are lifted to amber; medium and high
 * keep their amber/red.
 */
export type HouseholdGuarantorChipState =
	| 'recommended'
	| 'acceptable'
	| 'mixed'
	| 'unacceptable'

export interface HouseholdGuarantorChip {
	state: HouseholdGuarantorChipState
	/** 'Guarantor' once they exist; 'Guarantor Recommended' while none do. */
	label: string
	tone: GuarantorTone
}

export function deriveHouseholdGuarantorChip(input: {
	/** One entry per guarantor ON the household: true = acceptable, false =
	 * not acceptable, null = no verdict yet. */
	verdicts: ReadonlyArray<boolean | null>
	/** A guarantor has been requested (invite sent / marked required). */
	requested: boolean
	/** The applicant's `guarantorScore` — drives the recommendation colour. */
	guarantorScore: number | null
}): HouseholdGuarantorChip | null {
	if (input.verdicts.length > 0) {
		const judged = input.verdicts.filter((v): v is boolean => v !== null)
		if (judged.length === 0)
			return { state: 'mixed', label: 'Guarantor', tone: 'medium' }
		const acceptable = judged.filter(Boolean).length
		if (acceptable === judged.length) {
			return { state: 'acceptable', label: 'Guarantor', tone: 'low' }
		}
		if (acceptable === 0) {
			return { state: 'unacceptable', label: 'Guarantor', tone: 'high' }
		}
		return { state: 'mixed', label: 'Guarantor', tone: 'medium' }
	}

	if (!input.requested && !isGuarantorRecommended(input.guarantorScore)) {
		return null
	}
	const scoreTone = guarantorScoreTone(input.guarantorScore)
	return {
		state: 'recommended',
		label: 'Guarantor Recommended',
		// Never green — see above.
		tone: scoreTone === 'low' ? 'medium' : scoreTone,
	}
}

export type GuarantorTone = 'low' | 'medium' | 'high'

/**
 * Guarantor-score → risk tone, the shared thresholds of the legacy
 * `GuarantorScore` chip (ui/guarantor-score.tsx): ≤30 low (green), ≤70 medium
 * (amber), else high (red). A null/absent score defaults to 'medium' (amber) —
 * the historical chip colour — so the card never renders uncoloured.
 */
export function guarantorScoreTone(score: number | null): GuarantorTone {
	if (score == null) return 'medium'
	if (score <= 30) return 'low'
	if (score <= 70) return 'medium'
	return 'high'
}

// ---------------------------------------------------------------------------
// §3 Rental Guarantee tier (header "Rental Guarantee" chip popover, spec §18)
// ---------------------------------------------------------------------------

export interface GuaranteeTier {
	/** Guarantee rate the tenant qualifies for at this score. */
	percent: number
	/** Tier name shown on the right of the popover card. */
	label: string
	/** One-line eligibility blurb under the rate. */
	description: string
}

/**
 * Score → guarantee tier for the "Rental Guarantee Eligibility" popover. The
 * exact tier table is PRODUCT-DEFERRED (spec §18 — no backend/pricing yet);
 * this is a sensible interim mapping (higher score → lower rate). Score 75 →
 * "5% Standard Tier" matches the design mock. Adjust here when product ships
 * the real table.
 */
export function deriveGuaranteeTier(score: number): GuaranteeTier {
	if (score >= 80)
		return {
			percent: 2.5,
			label: 'Preferred Tier',
			description:
				'Strong applicant, minimal risk. Lock in guaranteed rent at our lowest rate and close off even the smallest chance of a missed payment.',
		}
	if (score >= 65)
		return {
			percent: 5,
			label: 'Standard Tier',
			description:
				'Solid profile with medium risk factors. Standard guarantee rate applies.',
		}
	if (score >= 50)
		return {
			percent: 8,
			label: 'Elevated Tier',
			description:
				'Higher-risk applicant. A filled unit at a higher rate still beats a vacant one. Approve with confidence and keep the rent coming.',
		}
	return {
		percent: 10,
		label: 'High-Risk Tier',
		description:
			'Significant risk factors. The highest guarantee rate applies.',
	}
}

// ---------------------------------------------------------------------------
// §5 Risk Flags & Alerts
// ---------------------------------------------------------------------------

export interface RiskFlagLists {
	red: string[]
	yellow: string[]
}

/**
 * Flag MESSAGES per panel — the design drops v1's severity-level badge
 * (recorded deviation, spec §7); panels render only when non-empty (v1
 * RiskFlagsSection parity). Salvage accepts `{message}` entries or bare
 * strings; empty/malformed entries are dropped.
 */
export function deriveRiskFlagLists(aiReportData: AiReportData): RiskFlagLists {
	const riskFlags = extractPredictions(aiReportData ?? null)?.riskFlags
	const messages = (list: unknown): string[] => {
		if (!Array.isArray(list)) return []
		return list
			.map((entry) => {
				if (typeof entry === 'string') return entry.trim()
				if (entry != null && typeof entry === 'object' && !Array.isArray(entry))
					return toText((entry as Record<string, unknown>).message)
				return ''
			})
			.filter(Boolean)
	}
	return { red: messages(riskFlags?.red), yellow: messages(riskFlags?.yellow) }
}

/**
 * Does any flag concern the applicant's BANK details? Gates the pre-screen
 * banner's "Request Additional Bank Account" CTA: the AI raises unrelated
 * flags too (address mismatch, no employment history), and offering a bank
 * request against those reads as a non-sequitur. Matches the wording the
 * model actually produces — "banking details", "bank account".
 */
export function mentionsBankAccount(messages: readonly string[]): boolean {
	return messages.some((message) => /\bbank(ing)?\b/i.test(message))
}

/**
 * Kind gate for the "Risk Flags & Alerts" card, shared by the page render
 * site (ApplicationDetailPage) and the individual PDF so the two can't
 * drift. Full screening always shows — the page card self-hides when empty,
 * the PDF renders its "No risk flags identified." empty state. Pre-screening
 * (2026-07-17): APPLICANT-type applications (primary/co-applicant) show the
 * card only when the AI actually produced flags — a flagless pre-screen
 * report/PDF renders nothing — and pre-screen guarantors keep the original
 * full-only hide. Flag entries are opaque here: only presence counts (the
 * page passes string lists, the PDF passes {level,message} lists).
 */
export function shouldShowRiskFlags(input: {
	screeningKind: ScreeningKind
	applicationType: string | null | undefined
	red: readonly unknown[]
	yellow: readonly unknown[]
	/** Which surface asks (client 2026-09-07). On the PAGE a pre-screen report
	 * already repeats its flags in the PreScreenFlagBanner at the top, so the
	 * body card is hidden for pre-screening there; the PDF has no banner and
	 * keeps the 2026-07-17 rule. Defaults to 'pdf' (the unchanged rule). */
	surface?: 'page' | 'pdf'
}): boolean {
	if (input.screeningKind === 'full') return true
	if (input.surface === 'page') return false
	if (input.applicationType === 'guarantor') return false
	return input.red.length > 0 || input.yellow.length > 0
}

// ---------------------------------------------------------------------------
// §6 Debt Summary (header chips + three sub-cards)
// ---------------------------------------------------------------------------

export interface DebtAccountView {
	type: string // sub-card title, e.g. 'Open & Revolving'
	/** Raw utilization label as carried, e.g. '92%'; '' when absent. */
	used: string
	/** Parsed bar fraction source (v1 DebtSummarySection:71-73 parseInt). */
	utilizationPercent: number | null
	balance: number | null
	limit: number | null
	monthlyPayments: number | null
	pastDue: number | null
	paymentStatus: string // e.g. 'Up To Date'; '' when absent
}

export interface DebtSummaryView {
	monthlyDebtPayment: number | null // header chip 'Monthly $X'
	totalDebtOwing: number | null // header chip 'Total Owing $Y'
	details: DebtAccountView[]
}

/**
 * Null when `predictions.debtSummary` is absent — v1 renders the section
 * only then (v1:5296-5298 → DebtSummarySection gate); this is the inner
 * presence check AFTER the kind gate (map §6).
 */
export function deriveDebtSummaryView(
	aiReportData: AiReportData,
): DebtSummaryView | null {
	const debtSummary = extractPredictions(aiReportData ?? null)?.debtSummary
	if (
		debtSummary == null ||
		typeof debtSummary !== 'object' ||
		Array.isArray(debtSummary)
	) {
		return null
	}
	return {
		monthlyDebtPayment: toAmount(debtSummary.monthlyDebtPayment),
		totalDebtOwing: toAmount(debtSummary.totalDebtOwing),
		details: toRowObjects(debtSummary.details).map((detail) => ({
			type: toText(detail.type),
			used: toText(detail.used),
			utilizationPercent: toAmount(detail.used),
			balance: toAmount(detail.balance),
			limit: toAmount(detail.limit),
			monthlyPayments: toAmount(detail.monthlyPayments),
			pastDue: toAmount(detail.pastDue),
			paymentStatus: toText(detail.paymentStatus),
		})),
	}
}

// ---------------------------------------------------------------------------
// §7/§8 Collections + Written-Off/Charge-Off tables (same row shape)
// ---------------------------------------------------------------------------

export interface CollectionRowView {
	creditor: string
	date: string // rendered as-is (v1 parity)
	amount: number | null
	originalAmount: number | null
	status: string // chip: paid → green, unpaid → red, else neutral (UI)
	remarks: string
}

function toCollectionRows(raw: unknown): CollectionRowView[] {
	return toRowObjects(raw).map((row) => ({
		creditor: toText(row.creditor),
		date: toText(row.date),
		amount: toAmount(row.amount),
		originalAmount: toAmount(row.originalAmount),
		status: toText(row.status),
		remarks: toText(row.remarks),
	}))
}

/** `predictions.collections.data` (helpers:24-32; v1 DebtSummarySection:160-215
 * renders the table only when non-empty). */
export function deriveCollectionRows(
	aiReportData: AiReportData,
): CollectionRowView[] {
	return toCollectionRows(
		extractPredictions(aiReportData ?? null)?.collections?.data,
	)
}

/** `predictions.writtenOffAccounts.data` — written-off / charge-off accounts.
 * (The upstream key is `writtenOffAccounts`, NOT `writeOffs`; same row shape as
 * collections. Empty/absent → the card presence-gates to hidden.) */
export function deriveWriteOffRows(
	aiReportData: AiReportData,
): CollectionRowView[] {
	return toCollectionRows(
		extractPredictions(aiReportData ?? null)?.writtenOffAccounts?.data,
	)
}

// ---------------------------------------------------------------------------
// §9 Bankruptcy & Insolvency table
// ---------------------------------------------------------------------------

export interface BankruptcyRowView {
	reported: string
	trustee: string
	assets: number | null
	liability: number | null
	status: string
	remarks: string
}

/** `predictions.bankruptcyAndInsolvency.data` (helpers:34-42; v1 table
 * DebtSummarySection:217-275). */
export function deriveBankruptcyRows(
	aiReportData: AiReportData,
): BankruptcyRowView[] {
	return toRowObjects(
		extractPredictions(aiReportData ?? null)?.bankruptcyAndInsolvency?.data,
	).map((row) => ({
		reported: toText(row.reported),
		trustee: toText(row.trustee),
		assets: toAmount(row.assets),
		liability: toAmount(row.liability),
		status: toText(row.status),
		remarks: toText(row.remarks),
	}))
}

// ---------------------------------------------------------------------------
// §7/§9 'Total Owing' chips — recorded design extrapolations (no per-table
// total exists in predictions): Σ amount / Σ liability. Null when no row
// carries a numeric value, so the UI can hide the chip.
// ---------------------------------------------------------------------------

export function sumCollectionsOwing(rows: CollectionRowView[]): number | null {
	const amounts = rows
		.map((row) => row.amount)
		.filter((amount): amount is number => amount != null)
	if (amounts.length === 0) return null
	return amounts.reduce((sum, amount) => sum + amount, 0)
}

export function sumBankruptcyOwing(rows: BankruptcyRowView[]): number | null {
	const liabilities = rows
		.map((row) => row.liability)
		.filter((liability): liability is number => liability != null)
	if (liabilities.length === 0) return null
	return liabilities.reduce((sum, liability) => sum + liability, 0)
}

// ---------------------------------------------------------------------------
// §4 Credit Report tile status
// ---------------------------------------------------------------------------

/** v1's availability semantics (resolveCreditData v1:235-293 →
 * VerificationSummaryContent:572-581): report data or a PDF blob exists. */
export function isCreditReportAvailable(credit: CreditSummary | null): boolean {
	return Boolean(credit && (credit.reportData != null || credit.blobPath))
}
